import styled from 'styled-components';
import { ComponentStatus } from './types';

type Tone = 'success' | 'warning' | 'danger' | 'neutral';

const toneVars: Record<Tone, { bg: string; ink: string; dot: string }> = {
  success: { bg: 'var(--success-soft)', ink: 'var(--success-ink)', dot: 'var(--success-indicator, var(--success))' },
  warning: { bg: 'var(--warning-soft)', ink: 'var(--warning-ink)', dot: 'var(--warning)' },
  danger: { bg: 'var(--danger-soft)', ink: 'var(--danger-ink)', dot: 'var(--danger)' },
  neutral: { bg: 'var(--brand-green-soft)', ink: 'var(--muted)', dot: 'var(--muted-2)' },
};

export const statusTone = (status: ComponentStatus): Tone => {
  if (status === 'operational') return 'success';
  if (status === 'degraded') return 'warning';
  return 'danger';
};

export const StyledStatusHero = styled.div<{ tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 24px;
  border-radius: var(--r-lg);
  background: ${({ tone }) => toneVars[tone].bg};
  border: 1px solid color-mix(in srgb, ${({ tone }) => toneVars[tone].dot} 28%, transparent);
`;

export const StyledStatusHeroDot = styled.span<{ tone: Tone }>`
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ tone }) => toneVars[tone].dot};
  box-shadow: 0 0 0 4px color-mix(in srgb, ${({ tone }) => toneVars[tone].dot} 22%, transparent);
`;

export const StyledStatusHeroBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledStatusHeroTitle = styled.h1`
  margin: 0;
  font-size: var(--t-xl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--ink);
`;

export const StyledStatusHeroSub = styled.p`
  margin: 0;
  font-size: var(--t-sm);
  color: var(--muted);
`;

export const StyledStatusEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 10px;
`;

export const StyledStatusSectionTitle = styled.h2`
  margin: 40px 0 14px;
  font-size: var(--t-lg);
  font-weight: 700;
  color: var(--ink);
`;

export const StyledStatusList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  background: var(--surface);
`;

export const StyledStatusRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;

  & + & { border-top: 1px solid var(--line); }
`;

export const StyledStatusRowName = styled.span`
  font-size: var(--t-base);
  font-weight: 500;
  color: var(--ink);
`;

export const StyledStatusPill = styled.span<{ tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 11px;
  border-radius: var(--r-full);
  font-size: var(--t-xs);
  font-weight: 600;
  background: ${({ tone }) => toneVars[tone].bg};
  color: ${({ tone }) => toneVars[tone].ink};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ tone }) => toneVars[tone].dot};
  }
`;

export const StyledStatusMeta = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: var(--t-xs);
  color: var(--muted-2);
`;

export const StyledStatusRefresh = styled.button`
  appearance: none;
  border: 1px solid var(--line);
  background: var(--surface);
  cursor: pointer;
  padding: 5px 12px;
  border-radius: var(--r-full);
  font-size: var(--t-xs);
  font-weight: 600;
  color: var(--ink);

  &:hover { border-color: var(--muted-2); }
  &:disabled { opacity: 0.55; cursor: default; }
`;
